/**
 * Tax Report — 501(c) Non-Profit
 *
 * Printable, downloadable tax report aligned to IRS Form 990 sections.
 * - Yearly or Quarterly periods
 * - Statement of Financial Activities
 * - Per-account breakdown
 * - Revenue & Expense by category
 * - Full itemised transaction list
 * - "Print" button → browser print dialog (Save as PDF available)
 * - "Download PDF" button → server-rendered PDF (consistent output)
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Loader2, Printer, Download, FileText, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatCurrency = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

export default function TaxReport() {
  const token = localStorage.getItem("token");

  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState("ALL"); // "ALL" = yearly, "1".."4" = quarter
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const years = useMemo(
    () => Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i),
    []
  );

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line
  }, [year, quarter]);

  const buildParams = () => {
    const params = { year };
    if (quarter !== "ALL") params.quarter = parseInt(quarter, 10);
    return params;
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/treasury/reports/tax-report`, {
        params: buildParams(),
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Failed to load tax report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    setPdfDownloading(true);
    try {
      const res = await axios.get(`${API}/treasury/reports/tax-report.pdf`, {
        params: buildParams(),
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const label = quarter === "ALL" ? `FY${year}` : `Q${quarter}-${year}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `BOH-Tax-Report-${label}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error(err);
      toast.error("PDF download failed");
    } finally {
      setPdfDownloading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Controls — hidden on print */}
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 print:hidden">
        <div className="flex-1">
          <p className="text-xs text-slate-400 mb-1">Tax Year</p>
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="w-full sm:w-40 bg-slate-800 border-slate-700" data-testid="tax-report-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={y.toString()}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <p className="text-xs text-slate-400 mb-1">Period</p>
          <Select value={quarter} onValueChange={setQuarter}>
            <SelectTrigger className="w-full sm:w-48 bg-slate-800 border-slate-700" data-testid="tax-report-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Full Year (Jan–Dec)</SelectItem>
              <SelectItem value="1">Q1 (Jan–Mar)</SelectItem>
              <SelectItem value="2">Q2 (Apr–Jun)</SelectItem>
              <SelectItem value="3">Q3 (Jul–Sep)</SelectItem>
              <SelectItem value="4">Q4 (Oct–Dec)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 sm:ml-auto">
          <Button
            variant="outline"
            onClick={handlePrint}
            disabled={!report || loading}
            data-testid="tax-report-print-btn"
            className="border-slate-600 bg-slate-800 text-white hover:bg-slate-700"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button
            onClick={handleDownloadPdf}
            disabled={!report || loading || pdfDownloading}
            data-testid="tax-report-pdf-btn"
            className="bg-green-600 hover:bg-green-700"
          >
            {pdfDownloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download PDF
          </Button>
        </div>
      </div>

      {/* Print-only header */}
      <div className="hidden print:block">
        <style>{`
          @page { size: letter; margin: 0.6in; }
          @media print {
            body, html { background: white !important; color: black !important; }
            .print\\:hidden { display: none !important; }
            .tax-print-page { background: white !important; color: black !important; }
            .tax-print-page * { color: black !important; background: white !important; border-color: #cbd5e1 !important; }
            .tax-print-page h1, .tax-print-page h2 { color: #0f172a !important; }
            .tax-print-page .tax-section { page-break-inside: avoid; }
            .tax-print-page .tax-section-tx { page-break-before: always; }
            .tax-print-page table { border-collapse: collapse; width: 100%; font-size: 9pt; }
            .tax-print-page th, .tax-print-page td { border: 0.5pt solid #cbd5e1 !important; padding: 4px 6px; }
            .tax-print-page th { background: #0f172a !important; color: white !important; }
            .tax-print-page .tax-income-header th { background: #065f46 !important; color: white !important; }
            .tax-print-page .tax-expense-header th { background: #991b1b !important; color: white !important; }
            .tax-print-page .tax-total-row td { background: #f1f5f9 !important; font-weight: bold; }
          }
        `}</style>
      </div>

      {/* Loading / Empty */}
      {loading ? (
        <div className="flex items-center justify-center p-8 print:hidden">
          <Loader2 className="w-8 h-8 text-green-400 animate-spin" />
        </div>
      ) : !report ? (
        <Card className="bg-slate-800 border-slate-700 print:hidden">
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">No data for this period.</p>
          </CardContent>
        </Card>
      ) : (
        <TaxReportPreview report={report} />
      )}
    </div>
  );
}

function TaxReportPreview({ report }) {
  const accountTotals = useMemo(() => {
    const acc = report.accounts || [];
    return {
      income: acc.reduce((s, a) => s + (a.period_income || 0), 0),
      expense: acc.reduce((s, a) => s + (a.period_expense || 0), 0),
      net: acc.reduce((s, a) => s + (a.period_net || 0), 0),
    };
  }, [report.accounts]);

  return (
    <div className="tax-print-page bg-white text-slate-900 rounded-lg shadow-xl p-6 sm:p-10 max-w-4xl mx-auto print:shadow-none print:p-0">
      {/* ---- Header ---- */}
      <div className="tax-section border-b border-slate-300 pb-4 mb-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
          {report.organization.name}
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          {report.organization.type} &nbsp;|&nbsp; Tax Report &nbsp;|&nbsp;{" "}
          <span className="font-semibold">{report.period.label}</span>
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Period: {report.period.start_date} through {report.period.end_date_exclusive}{" "}
          (exclusive)
        </p>
        <p className="text-xs text-slate-500">
          Generated {report.generated_at?.slice(0, 19).replace("T", " ")} UTC by{" "}
          {report.generated_by}
        </p>
      </div>

      {/* ---- Statement of Financial Activities ---- */}
      <div className="tax-section mb-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-3">
          Statement of Financial Activities
        </h2>
        <table className="w-full text-sm" data-testid="tax-summary-table">
          <tbody>
            <tr className="border-b border-slate-200">
              <td className="py-2">Total Revenue (Income)</td>
              <td className="py-2 text-right font-medium">
                {formatCurrency(report.income.total)}
              </td>
            </tr>
            <tr className="border-b border-slate-200">
              <td className="py-2">Total Expenses</td>
              <td className="py-2 text-right font-medium">
                {formatCurrency(report.expenses.total)}
              </td>
            </tr>
            <tr className="border-b-2 border-slate-900">
              <td className="py-2 font-bold">Net Income / (Loss)</td>
              <td
                className={`py-2 text-right font-bold ${
                  (report.net || 0) >= 0 ? "text-emerald-700" : "text-red-700"
                }`}
              >
                {formatCurrency(report.net)}
              </td>
            </tr>
            <tr className="bg-slate-50">
              <td className="py-2 font-bold">Total Current Balance (all accounts)</td>
              <td className="py-2 text-right font-bold">
                {formatCurrency(report.total_balance)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ---- Account Balances & Activity ---- */}
      <div className="tax-section mb-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-3">
          Account Balances &amp; Activity
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm" data-testid="tax-accounts-table">
            <thead>
              <tr>
                <th className="bg-slate-900 text-white text-left p-2">Account</th>
                <th className="bg-slate-900 text-white text-left p-2">Type</th>
                <th className="bg-slate-900 text-white text-right p-2">Period Income</th>
                <th className="bg-slate-900 text-white text-right p-2">Period Expense</th>
                <th className="bg-slate-900 text-white text-right p-2">Period Net</th>
                <th className="bg-slate-900 text-white text-right p-2">Current Balance</th>
              </tr>
            </thead>
            <tbody>
              {report.accounts.map((a) => (
                <tr key={a.id} className="border-b border-slate-200">
                  <td className="p-2">{a.name}</td>
                  <td className="p-2 capitalize">{a.type}</td>
                  <td className="p-2 text-right text-emerald-700">
                    {formatCurrency(a.period_income)}
                  </td>
                  <td className="p-2 text-right text-red-700">
                    {formatCurrency(a.period_expense)}
                  </td>
                  <td
                    className={`p-2 text-right font-medium ${
                      a.period_net >= 0 ? "text-emerald-700" : "text-red-700"
                    }`}
                  >
                    {formatCurrency(a.period_net)}
                  </td>
                  <td className="p-2 text-right font-medium">
                    {formatCurrency(a.current_balance)}
                  </td>
                </tr>
              ))}
              <tr className="tax-total-row bg-slate-100 font-bold">
                <td className="p-2">TOTAL</td>
                <td className="p-2"></td>
                <td className="p-2 text-right">{formatCurrency(accountTotals.income)}</td>
                <td className="p-2 text-right">{formatCurrency(accountTotals.expense)}</td>
                <td className="p-2 text-right">{formatCurrency(accountTotals.net)}</td>
                <td className="p-2 text-right">{formatCurrency(report.total_balance)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ---- Revenue by Category ---- */}
      <div className="tax-section mb-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-3">
          Revenue by Category{" "}
          <span className="text-xs font-normal text-slate-500">(Form 990 Part VIII)</span>
        </h2>
        {report.income.by_category.length > 0 ? (
          <table className="w-full text-sm" data-testid="tax-income-table">
            <thead className="tax-income-header">
              <tr>
                <th className="bg-emerald-800 text-white text-left p-2">Category</th>
                <th className="bg-emerald-800 text-white text-right p-2"># Trans.</th>
                <th className="bg-emerald-800 text-white text-right p-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {report.income.by_category.map((c) => (
                <tr key={c.category_id} className="border-b border-slate-200">
                  <td className="p-2">{c.category_name}</td>
                  <td className="p-2 text-right text-slate-500">{c.count}</td>
                  <td className="p-2 text-right text-emerald-700 font-medium">
                    {formatCurrency(c.total)}
                  </td>
                </tr>
              ))}
              <tr className="tax-total-row bg-emerald-50 font-bold">
                <td className="p-2">TOTAL REVENUE</td>
                <td className="p-2"></td>
                <td className="p-2 text-right">{formatCurrency(report.income.total)}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-slate-500 italic">No revenue recorded in this period.</p>
        )}
      </div>

      {/* ---- Expenses by Category ---- */}
      <div className="tax-section mb-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-3">
          Expenses by Category{" "}
          <span className="text-xs font-normal text-slate-500">(Form 990 Part IX)</span>
        </h2>
        {report.expenses.by_category.length > 0 ? (
          <table className="w-full text-sm" data-testid="tax-expense-table">
            <thead className="tax-expense-header">
              <tr>
                <th className="bg-red-800 text-white text-left p-2">Category</th>
                <th className="bg-red-800 text-white text-right p-2"># Trans.</th>
                <th className="bg-red-800 text-white text-right p-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {report.expenses.by_category.map((c) => (
                <tr key={c.category_id} className="border-b border-slate-200">
                  <td className="p-2">{c.category_name}</td>
                  <td className="p-2 text-right text-slate-500">{c.count}</td>
                  <td className="p-2 text-right text-red-700 font-medium">
                    {formatCurrency(c.total)}
                  </td>
                </tr>
              ))}
              <tr className="tax-total-row bg-red-50 font-bold">
                <td className="p-2">TOTAL EXPENSES</td>
                <td className="p-2"></td>
                <td className="p-2 text-right">{formatCurrency(report.expenses.total)}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-slate-500 italic">No expenses recorded in this period.</p>
        )}
      </div>

      {/* ---- Itemised Transactions ---- */}
      <div className="tax-section tax-section-tx">
        <h2 className="text-lg font-semibold text-slate-800 mb-1">
          Itemised Transactions
          <span className="ml-2 text-xs font-normal text-slate-500">
            ({report.transaction_count} entries)
          </span>
        </h2>
        <p className="text-xs text-slate-500 mb-3">
          Supporting detail for revenue and expense lines. Sorted chronologically.
        </p>
        {report.transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="tax-tx-table">
              <thead>
                <tr>
                  <th className="bg-slate-900 text-white text-left p-2">Date</th>
                  <th className="bg-slate-900 text-white text-left p-2">Type</th>
                  <th className="bg-slate-900 text-white text-left p-2">Account</th>
                  <th className="bg-slate-900 text-white text-left p-2">Category</th>
                  <th className="bg-slate-900 text-white text-left p-2">Payee / Description</th>
                  <th className="bg-slate-900 text-white text-left p-2">Ref&nbsp;#</th>
                  <th className="bg-slate-900 text-white text-right p-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {report.transactions.map((t) => (
                  <tr key={t.id} className="border-b border-slate-200 align-top">
                    <td className="p-2 whitespace-nowrap">{(t.date || "").slice(0, 10)}</td>
                    <td className="p-2 capitalize">{t.type}</td>
                    <td className="p-2">{t.account_name}</td>
                    <td className="p-2">{t.category_name}</td>
                    <td className="p-2">
                      {t.vendor_payee && (
                        <div className="font-semibold">{t.vendor_payee}</div>
                      )}
                      <div className="text-slate-600">{t.description}</div>
                    </td>
                    <td className="p-2">{t.reference_number}</td>
                    <td
                      className={`p-2 text-right font-medium whitespace-nowrap ${
                        t.type === "income" ? "text-emerald-700" : "text-red-700"
                      }`}
                    >
                      {formatCurrency(t.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-500 italic">No transactions recorded in this period.</p>
        )}
      </div>

      {/* ---- Footer ---- */}
      <div className="tax-section mt-8 pt-4 border-t border-slate-300">
        <div className="flex items-start gap-2 text-xs text-slate-500">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>
            This report was generated from internal treasury records and is intended to assist
            with the preparation of IRS Form 990 (or 990-EZ / 990-N as applicable). It is the
            filer's responsibility to verify all amounts against bank statements and supporting
            receipts.
          </p>
        </div>
      </div>
    </div>
  );
}
